import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";
import GetRide from "@/components/GetRide";
import {
  CheckCircle2, Loader2, MapPin, Calendar, Users, Star, Ticket, ArrowRight, Sparkles,
} from "lucide-react";

export default function BookingConfirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`)
      .then((r) => setBooking(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <p className="font-heading text-xl text-white mb-3">Booking not found</p>
          <Link to="/dashboard" className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm">Go to dashboard</Link>
        </div>
      </div>
    );
  }
  if (!booking) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const qrPayload = JSON.stringify({ t: "carivisor-checkin", b: booking.id, code: booking.checkin_code });

  return (
    <div className="min-h-screen bg-[#070A18]">
      <div className="gradient-hero">
        <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-12 pb-8 text-center fade-up">
          <div className="w-16 h-16 rounded-full btn-neon flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white">
            {booking.payment_status === "paid" ? "Booking confirmed!" : "Booking reserved"}
          </h1>
          <p className="text-slate-300 mt-2">Show the QR code below at check-in. <span className="text-gradient-cool font-semibold">See it. Book it. Go.</span></p>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 -mt-2">
          {/* Ticket / QR */}
          <div className="lg:col-span-2">
            <div className="glass neon-border-cool rounded-2xl p-6 text-center sticky top-24" data-testid="checkin-qr-card">
              <div className="flex items-center justify-center gap-2 text-cyan-300 mb-4">
                <Ticket className="w-4 h-4" />
                <span className="font-mono-caption text-xs uppercase tracking-[0.2em]">Check-in Pass</span>
              </div>
              <div className="bg-white rounded-2xl p-5 inline-block" data-testid="qr-code">
                <QRCodeSVG value={qrPayload} size={196} level="M" includeMargin={false} fgColor="#070A18" />
              </div>
              <p className="font-mono-caption text-lg tracking-[0.25em] text-white mt-4" data-testid="checkin-code">
                {booking.checkin_code}
              </p>
              <p className="text-xs text-slate-500 mt-1">Present this code to your host on arrival</p>
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-3 space-y-5">
            <div className="glass rounded-2xl overflow-hidden">
              <img src={booking.listing_image} alt="" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h2 className="font-heading text-2xl font-extrabold text-white">{booking.listing_title}</h2>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-300">
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-300" /> {booking.location || booking.island}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-orange-400" /> {booking.date} · {booking.time}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-fuchsia-400" /> {booking.guests} guests</span>
                </div>

                <div className="mt-5 pt-5 border-t border-[#232A4D] space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>${booking.price?.toLocaleString()} {booking.price_type === "flat" ? "flat" : `× ${booking.guests} travelers`}</span>
                    <span>${booking.gross_amount?.toLocaleString()}</span>
                  </div>
                  {booking.discount > 0 && (
                    <div className="flex justify-between text-cyan-300">
                      <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Points redeemed ({booking.points_redeemed?.toLocaleString()} pts)</span>
                      <span>-${booking.discount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-heading font-bold text-white text-lg pt-2 border-t border-[#232A4D]">
                    <span>Total paid</span>
                    <span className="price-red" data-testid="confirm-total">${booking.charge_amount?.toLocaleString()}</span>
                  </div>
                </div>

                <div className="mt-4 glass rounded-xl p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-300 flex items-center gap-2"><Star className="w-4 h-4 fill-[#22E67A] text-[#22E67A]" /> Points earned this trip</span>
                  <span className="font-heading font-bold rating-green" data-testid="points-earned">+{booking.points_earned?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <GetRide booking={booking} />

            <div className="flex gap-3">
              <Link to="/dashboard" data-testid="confirm-view-trips" className="flex-1 btn-neon rounded-xl py-3.5 font-semibold text-center flex items-center justify-center gap-2">
                View my trips <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => navigate("/browse")} className="flex-1 glass rounded-xl py-3.5 font-semibold text-slate-200 hover:text-cyan-300">
                Book another
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
